import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import { getData, getErrorMessage, isSuccess, rfidAdminApi } from "../../hooks/rfidAdminApi";
import "./RfidAdmin.css";

const ClassStudentAdmin = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [classForm] = Form.useForm();
  const [studentForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(selectedClassId)),
    [classes, selectedClassId]
  );

  const loadClasses = async () => {
    try {
      setLoading(true);
      const res = await rfidAdminApi.getClasses();
      const rows = getData(res);
      setClasses(rows);
      if (!selectedClassId && rows.length > 0) {
        setSelectedClassId(rows[0].id);
      }
    } catch (error) {
      messageApi.error(getErrorMessage(error, "반 목록을 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (classId = selectedClassId) => {
    if (!classId) {
      setStudents([]);
      return;
    }
    try {
      setLoading(true);
      const res = await rfidAdminApi.getStudents(classId);
      setStudents(getData(res));
    } catch (error) {
      messageApi.error(getErrorMessage(error, "학생 목록을 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStudents(selectedClassId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  const handleCreateClass = async (values) => {
    try {
      const payload = {
        name: values.name.trim(),
        schoolYear: Number(values.schoolYear),
        memo: values.memo || "",
      };
      const res = await rfidAdminApi.createClass(payload);
      if (!isSuccess(res)) throw new Error(res?.error || "반 생성 실패");
      messageApi.success("반이 생성되었습니다.");
      classForm.resetFields();
      await loadClasses();
      setSelectedClassId(res.data?.id || selectedClassId);
    } catch (error) {
      messageApi.error(getErrorMessage(error, "반 생성 중 오류가 발생했습니다."));
    }
  };

  const handleDeleteClass = async (classId) => {
    try {
      const res = await rfidAdminApi.deleteClass(classId);
      if (!isSuccess(res)) throw new Error(res?.error || "반 삭제 실패");
      messageApi.success("반이 삭제되었습니다.");
      if (String(selectedClassId) === String(classId)) {
        setSelectedClassId(null);
        setStudents([]);
      }
      await loadClasses();
    } catch (error) {
      messageApi.error(getErrorMessage(error, "반 삭제 중 오류가 발생했습니다."));
    }
  };

  const handleCreateStudent = async (values) => {
    if (!selectedClassId) {
      messageApi.warning("먼저 반을 선택해주세요.");
      return;
    }

    try {
      const payload = {
        classId: String(selectedClassId).trim(),
        studentNo: values.studentNo === undefined || values.studentNo === null ? null : Number(values.studentNo),
        name: values.name.trim(),
        memo: values.memo || "",
      };
      console.log("student create payload", payload);
      const res = await rfidAdminApi.createStudent(payload);
      if (!isSuccess(res)) throw new Error(res?.error || "학생 등록 실패");
      messageApi.success("학생이 등록되었습니다.");
      studentForm.resetFields();
      await loadStudents(selectedClassId);
      await loadClasses();
    } catch (error) {
      messageApi.error(getErrorMessage(error, "학생 등록 중 오류가 발생했습니다."));
    }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      const res = await rfidAdminApi.deleteStudent(studentId);
      if (!isSuccess(res)) throw new Error(res?.error || "학생 삭제 실패");
      messageApi.success("학생이 삭제되었습니다.");
      await loadStudents(selectedClassId);
      await loadClasses();
    } catch (error) {
      messageApi.error(getErrorMessage(error, "학생 삭제 중 오류가 발생했습니다."));
    }
  };

  const classColumns = [
    { title: "학년", dataIndex: "school_year", width: 90 },
    { title: "반", dataIndex: "name" },
    { title: "학생 수", dataIndex: "student_count", width: 90 },
    {
      title: "관리",
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="반 삭제"
          description="학생과 활성 배정도 함께 정리됩니다. 삭제할까요?"
          okText="삭제"
          cancelText="취소"
          onConfirm={() => handleDeleteClass(record.id)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const studentColumns = [
    { title: "번호", dataIndex: "student_no", width: 80, render: (v) => v || "-" },
    { title: "이름", dataIndex: "name" },
    {
      title: "상태",
      dataIndex: "is_active",
      width: 90,
      render: (v) => (v ? <Tag color="green">활성</Tag> : <Tag>비활성</Tag>),
    },
    {
      title: "배정 RFID 태그",
      width: 160,
      render: (_, record) =>
        record.tag_label ? (
          <Space direction="vertical" size={0}>
            <span>{record.tag_label}</span>
            <span className="rfid-muted">{record.epc}</span>
          </Space>
        ) : (
          <Tag>미배정</Tag>
        ),
    },
    { title: "메모", dataIndex: "memo", ellipsis: true },
    {
      title: "관리",
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="학생 삭제"
          description="활성 RFID 태그 배정은 해제되고 기록은 유지됩니다. 삭제할까요?"
          okText="삭제"
          cancelText="취소"
          onConfirm={() => handleDeleteStudent(record.id)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="rfid-page">
      {contextHolder}
      <div className="rfid-page-header">
        <div>
          <h1 className="rfid-page-title">반/학생 관리</h1>
          <p className="rfid-page-desc">반을 만들고, 반별 학생 명단을 등록합니다.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => { loadClasses(); loadStudents(); }}>
          새로고침
        </Button>
      </div>

      <div className="rfid-grid-2">
        <Card title="반 생성" bordered>
          <Form form={classForm} layout="vertical" onFinish={handleCreateClass}>
            <Form.Item name="schoolYear" label="학년" rules={[{ required: true, message: "학년를 입력해주세요." }]}>
              <InputNumber style={{ width: "100%" }}  placeholder="예: 2" />
            </Form.Item>
            <Form.Item name="name" label="반 이름" rules={[{ required: true, message: "반 이름을 입력해주세요." }]}>
              <Input placeholder="예: 1반" />
            </Form.Item>
            <Form.Item name="memo" label="메모">
              <Input.TextArea rows={3} placeholder="선택 입력" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block icon={<TeamOutlined />}>
              반 생성
            </Button>
          </Form>
        </Card>

        <Card
          title={
            <div className="rfid-card-title">
              <span>반 목록</span>
              <Select
                style={{ minWidth: 220 }}
                placeholder="반 선택"
                value={selectedClassId || undefined}
                onChange={setSelectedClassId}
                options={classes.map((item) => ({
                  value: item.id,
                  label: `${item.school_year} ${item.name}`,
                }))}
              />
            </div>
          }
        >
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={classColumns}
            dataSource={classes}
            pagination={{ pageSize: 8 }}
            onRow={(record) => ({ onClick: () => setSelectedClassId(record.id) })}
            rowClassName={(record) => String(record.id) === String(selectedClassId) ? "ant-table-row-selected" : ""}
          />
        </Card>
      </div>

      <div className="rfid-section-gap rfid-grid-2">
        <Card title={`학생 등록${selectedClass ? `` : ""}`}>
          <Form form={studentForm} layout="vertical" onFinish={handleCreateStudent}>
            <Form.Item label="선택된 반">
              <Input value={selectedClass ? `${selectedClass.school_year} - ${selectedClass.name}` : "반을 선택해주세요"} disabled />
            </Form.Item>
            <Form.Item name="studentNo" label="번호">
              <InputNumber style={{ width: "100%" }} min={1} placeholder="예: 1" />
            </Form.Item>
            <Form.Item name="name" label="학생 이름" rules={[{ required: true, message: "학생 이름을 입력해주세요." }]}>
              <Input placeholder="예: 김민수" />
            </Form.Item>
            <Form.Item name="memo" label="메모">
              <Input.TextArea rows={3} placeholder="선택 입력" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block disabled={!selectedClassId}>
              학생 등록
            </Button>
          </Form>
        </Card>

        <Card title={`학생 목록${selectedClass ? `` : ""}`}>
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={studentColumns}
            dataSource={students}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default ClassStudentAdmin;
